import { InfiniteData, useInfiniteQuery, UseInfiniteQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { ExerciseDto } from '../../../shared/dtos/exercise.dto';
import { PagedDto } from '../../../shared/dtos/paged.dto';
import { ExerciseSearchRequest } from '../../../shared/requests/exerciseSearch.request';
import { useAuthFetchFromRestAPI } from '../util/api';
import { ScopeKey } from '../util/types';

export const EXERCISE_PAGE_SIZE: number = 20;
export const MIN_SEARCH_LENGTH: number = 3;

export const exerciseSearchQueryKeys = {
  all: ['exercise-search'] as const,
  list: (scope: ScopeKey, searchKey: string, tagIds: string[]) =>
    [...exerciseSearchQueryKeys.all, scope, searchKey, tagIds] as const,
}

export function normalizeExerciseFilter(selectedByCategory: Record<string, string[]>): Record<string, string[]> {
  const normalized: Record<string, string[]> = {};

  for (const [categoryId, tagIds] of Object.entries(selectedByCategory)) {
    const unique: string[] = Array.from(new Set(tagIds)).sort();

    if (unique.length > 0) {
      normalized[categoryId] = unique;
    }
  }

  return normalized;
}

type ExerciseSearchParams = {
  scope: ScopeKey;
  searchKey: string;
  selectedByCategory: Record<string, string[]>;
  enabled: boolean;
};

export function useExerciseSearch({
  scope,
  searchKey,
  selectedByCategory,
  enabled,
}: ExerciseSearchParams): UseInfiniteQueryResult<InfiniteData<PagedDto<ExerciseDto>>> {
  const { authFetch } = useAuthFetchFromRestAPI();

  const filter: Record<string, string[]> = useMemo(
    () => normalizeExerciseFilter(selectedByCategory),
    [selectedByCategory]
  );

  const flatTagIds: string[] = useMemo(() => Object.values(filter).flat().sort(), [filter]);

  const effectiveSearchKey: string = searchKey.trim().length >= MIN_SEARCH_LENGTH ? searchKey.trim() : '';

  return useInfiniteQuery({
    queryKey: exerciseSearchQueryKeys.list(scope, effectiveSearchKey, flatTagIds),
    initialPageParam: 0,
    queryFn: async ({ pageParam }): Promise<PagedDto<ExerciseDto>> => {
      const body: ExerciseSearchRequest = {
        page: pageParam,
        pageSize: EXERCISE_PAGE_SIZE,
        onlyActive: true,
        searchKey: effectiveSearchKey || undefined,
        filter: Object.keys(filter).length > 0 ? filter : undefined,
      };

      try {
        return await authFetch(`/api/v1/exercise/${scope}/search`, { method: 'POST', body });
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    getNextPageParam: (lastPage: PagedDto<ExerciseDto>, allPages: PagedDto<ExerciseDto>[]) => {
      const loaded: number = allPages.reduce((sum: number, page: PagedDto<ExerciseDto>) => sum + page.items.length, 0);
      const wasFullPage: boolean = lastPage.items.length === lastPage.pageSize;

      if (!wasFullPage || loaded >= lastPage.totalCount) {
        return undefined;
      }

      return allPages.length;
    },
    enabled,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    refetchOnMount: false,
  })
}
